import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Button,
    Flex,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Stack
} from "@chakra-ui/react";
import { FaCheck, FaEdit, FaPrint, FaTrashAlt } from "react-icons/fa";
import { IoIosArrowDown } from "react-icons/io";
import { AddIcon } from "@chakra-ui/icons";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import ListPageCardHeader from "components/Card/ListPageCardHeader";
import ConfirmDialog from "components/Dialog/ConfirmDialog";
import Loading from "components/Loading/Loading";
import SearchTable2 from "components/Tables/SearchTable2";
import { useCustomToast } from "components/CustomToast/CustomToast";
import { ROUTES } from "constants/routes-path";
import { getEmptyPageResponse, toTablePageableData } from "utils";
import { violationCardPaymentsService } from "services/violationCardPaymentsService";
import { ViolationCardPaymentStatus } from "constants/cards";

const ViolationCardPaymentsList = () => {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useState({
        orderBy: 'data_infracao',
        orderDirection: 'desc'
    });
    const dialogRef = useRef(undefined);
    const [response, setResponse] = useState(undefined);
    const navigate = useNavigate();
    const showToast = useCustomToast();
    const [violationCardPaymentToDelete, setViolationCardPaymentToDelete] = useState(null);
    const [isLoadingPrintReceipt, setIsLoadingPrintReceipt] = useState(false);
    const [isLoadingExportCardPayments, setIsLoadingExportCardPayments] = useState(false);
    const [isLoadingExportCardPaymentsValues, setIsLoadingExportCardPaymentsValues] = useState(false);
    const isLoadingActions = isLoadingPrintReceipt || isLoadingExportCardPayments || isLoadingExportCardPaymentsValues;

    const handlePrintReceipt = async (id) => { };

    const exportCardPayments = async () => { };

    const exportCardPaymentsValues = async () => { };

    const handleRemove = (violationCardPaymentId) => {
        setViolationCardPaymentToDelete(violationCardPaymentId);
        dialogRef.current?.displayDialog({
            title: t("violation-card-payments.list.actions.remove-card-payment"),
            message: t("system.actions.remove-confirm"),
        });
    };

    const handleConfirmRemove = async () => { };

    const columns = useMemo(() => ([
        {
            Header: t("violation-card-payments.list.table.header.payment-type"),
            accessor: "origem_pagamento",
        },
        {
            Header: t("violation-card-payments.list.table.header.installments"),
            accessor: "parcelas",
        },
        {
            Header: t("violation-card-payments.list.table.header.final-value"),
            accessor: "valor_final",
        },
        {
            Header: t("violation-card-payments.list.table.header.status"),
            accessor: "status",
            disableSortBy: true,
        },
        {
            Header: t("violation-card-payments.list.table.header.created-by"),
            accessor: "nome",
        },
        {
            Header: t("violation-card-payments.list.table.header.created-date"),
            accessor: "data_criacao",
        },
        {
            Header: t("system.labels.actions"),
            disableSortBy: true,
            actions: [
                {
                    label: t('system.actions.edit'),
                    onClick: (row) => navigate(ROUTES.violationCardPaymentsEditPath.replace(":id", row.original.id)),
                    visible: (row) => row?.original?.status !== ViolationCardPaymentStatus.FINALIZED.value,
                    icon: <FaEdit />
                },
                {
                    label: t('violation-card-payments.list.actions.finish'),
                    visible: (row) => row?.original?.status !== ViolationCardPaymentStatus.FINALIZED.value,
                    icon: <FaCheck />
                },
                {
                    label: t('system.actions.remove'),
                    onClick: (row) => { handleRemove(row.original.id) },
                    visible: (row) => row?.original?.status !== ViolationCardPaymentStatus.FINALIZED.value,
                    icon: <FaTrashAlt color="#E53E3E" />,
                },
                {
                    label: t('system.actions.print'),
                    onClick: handlePrintReceipt,
                    visible: (row) => row?.original?.status === ViolationCardPaymentStatus.FINALIZED.value,
                    icon: <FaPrint />
                }
            ]
        }
    ]), [t]);

    const newPaymentViolation = () => {
        navigate(ROUTES.violationCardPaymentsPath + ROUTES.violationCardPaymentsNew);
    }

    const fetchData = async (params = {}) => {
        if (response) setResponse(undefined);

        params = { ...searchParams, ...params };
        setSearchParams(params);

        setResponse(getEmptyPageResponse(searchParams));
    };

    const updateSearchParams = (params = {}) => {
        const page = Math.min(params?.page ?? 1, response?.total_pages ?? Infinity) || 1;
        if (params) setSearchParams({ ...searchParams, ...params, page: page });
        if (params.orderBy && params.orderBy in orderByMap) params.orderBy = orderByMap[params.orderBy];
    }

    useEffect(() => {
        fetchData();
    }, []);

    return (
        <Flex direction="column" gap="5" pt={{ base: "150px", lg: "90px" }}>
            <Flex w="100%" px="30px" justify="space-between" align="center" gap="4" direction={{ sm: "column", md: "row" }}>
                <Button
                    w="230px"
                    variant="list-primary"
                    leftIcon={<AddIcon fontSize="sm" />}
                    alignSelf={{ sm: "flex-start", lg: null }}
                    onClick={newPaymentViolation}
                >
                    {t("violation-card-payments.actions.new-payment-violation")}
                </Button>

                <Stack direction="row" spacing="10px" alignSelf={{ sm: "flex-start", lg: "auto" }}>
                    <Menu>
                        <MenuButton
                            w="125px"
                            variant="list-primary"
                            rightIcon={<IoIosArrowDown />}
                            as={Button}
                        >
                            {t("system.labels.actions")}
                        </MenuButton>

                        <MenuList variant="light">
                            <MenuItem onClick={exportCardPayments} icon={<AddIcon />}>
                                {t("violation-card-payments.actions.export-excel-report")}
                            </MenuItem>

                            <MenuItem onClick={exportCardPaymentsValues} icon={<AddIcon />}>
                                {t("violation-card-payments.actions.export-excel-report-values")}
                            </MenuItem>
                        </MenuList>
                    </Menu>
                </Stack>
            </Flex>

            <Card overflowX={{ sm: "scroll", lg: "hidden" }}>
                <ListPageCardHeader title={t("violation-card-payments.title")} subtitle={t("violation-card-payments.description")} />

                <CardBody>
                    {response !== undefined ? (
                        <Box position="relative">
                            <SearchTable2
                                columnsData={columns}
                                disableSearch={true}
                                tableData={response?._embedded?.content}
                                pageableData={toTablePageableData(response, { defaultField: "data_infracao", ...searchParams })}
                                onChange={(params) => {
                                    updateSearchParams(params);
                                }}
                            />
                            {isLoadingActions && (
                                <Box
                                    position="fixed"
                                    top="0"
                                    left="0"
                                    right="0"
                                    bottom="0"
                                    bg="rgba(255, 255, 255, 0.6)"
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    zIndex="overlay"
                                >
                                    <Loading maxH={{ sm: "100px", md: "350px" }} />
                                </Box>
                            )}
                        </Box>
                    ) : (
                        <Loading maxH={{ sm: "100px", md: "350px" }} />
                    )}
                </CardBody>
            </Card>

            <ConfirmDialog ref={dialogRef} onConfirm={handleConfirmRemove} confirmButtonProps={{ colorScheme: "red" }} />
        </Flex>
    );
};

export default ViolationCardPaymentsList;
